// src/pages/Blog.jsx
import React, { useEffect, useState } from 'react';
import Navbar from './Navbar';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import '../styles/blog.css';
import Seo from '../components/Seo';

const normalizeCategory = (value = '') => {
  const text = value.toLowerCase();
  if (text.includes('thibb') || text.includes('nabawi')) return 'Thibbun Nabawi';
  if (text.includes('gangguan') || text.includes('keluhan') || text.includes('jin') || text.includes('sihir')) {
    return 'Gangguan & Keluhan';
  }
  if (text.includes('edukasi') || text.includes('ruqyah')) return 'Edukasi Ruqyah';
  if (text.includes('umum')) return 'Umum';
  return value ? 'Umum' : '';
};

export default function Blog() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Semua');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      const q = query(
        collection(db, 'articles'),
        where('status', '==', 'published'),
        orderBy('date', 'desc')
      );
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setArticles(data);
      setLoading(false);
    };
    fetchArticles();
  }, []);

  const categories = [
    'Semua',
    ...new Set(articles.map(a => normalizeCategory(a.category)).filter(Boolean))
  ];

  const normalizedSearch = search.trim().toLowerCase();

  const filtered = articles.filter(a => {
    const title = String(a.title || '').toLowerCase();
    const excerpt = String(a.excerpt || '').toLowerCase();
    const articleCategory = normalizeCategory(a.category) || 'Umum';

    return (
      (selectedCategory === 'Semua' || articleCategory === selectedCategory) &&
      (normalizedSearch === '' ||
        title.includes(normalizedSearch) ||
        excerpt.includes(normalizedSearch))
    );
  });

  const isAll = pageSize === 'all';
  const totalItems = filtered.length;
  const totalPages = isAll ? 1 : Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedCategory, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const startIndex = isAll ? 0 : (currentPage - 1) * pageSize;
  const endIndex = isAll ? totalItems : startIndex + pageSize;
  const paginated = isAll ? filtered : filtered.slice(startIndex, endIndex);

  return (
    <>
      <Seo
        title="Artikel Ruqyah Syar'i & Kesehatan Ruhani"
        description="Kumpulan artikel edukasi ruqyah syar'i, gangguan batin, dan thibbun nabawi."
        canonical="/blog"
        image="https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1600&q=80"
      />
      <Navbar />
      <section className="blog-hero text-white">
        <div className="blog-overlay">
          <div className="container blog-hero-content">
            <span className="blog-hero-badge">
              <i className="bi bi-journal-text"></i>
              Wawasan Ruqyah Syar&apos;i
            </span>
            <h1 className="display-4 fw-bold">Artikel & Wawasan</h1>
            <p className="lead mt-2">
              Kumpulan artikel Islami seputar ruqyah, kesehatan batin, dan perlindungan diri.
            </p>
            <div className="blog-hero-chips">
              <span>Ruqyah Syar&apos;i</span>
              <span>Kesehatan Ruhani</span>
              <span>Ikhtiar & Sunnah</span>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container">
          <div className="row mb-4">
            <div className="col-md-8 mx-auto">
              <div className="blog-search">
                <i className="bi bi-search"></i>
                <input
                  type="text"
                  className="form-control form-control-lg"
                  placeholder="Cari artikel..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="row mb-4 justify-content-center">
            <div className="col-auto d-flex flex-wrap gap-2 justify-content-center">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`btn ${selectedCategory === cat ? 'btn-success' : 'btn-outline-success'} btn-sm rounded-pill`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="blog-pagination-toolbar">
            <div className="pagination-info">
              Menampilkan {totalItems === 0 ? 0 : startIndex + 1}–{Math.min(endIndex, totalItems)} dari {totalItems} artikel
            </div>
            <div className="pagination-controls">
              <label className="me-2">Tampil</label>
              <select
                className="form-select form-select-sm"
                value={pageSize}
                onChange={e => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value="all">Semua</option>
              </select>
            </div>
          </div>

          {loading ? (
            <p className="text-center">Memuat artikel...</p>
          ) : (
            <div className="row g-4">
              {paginated.length ? (
                paginated.map(a => {
                  const displayCategory = normalizeCategory(a.category) || 'Umum';
                  return (
                  <div className="col-md-4" key={a.id}>
                    <Link to={`/post/${a.slug || a.id}`} className="article-card-link">
                      <article className="article-card shadow-sm h-100">
                        {a.image ? (
                          <img src={a.image} alt={a.title} className="article-img" loading="lazy" />
                        ) : (
                          <div className="article-img placeholder-bg" />
                        )}
                        <div className="p-3">
                          <p className="article-meta">
                            <i className="bi bi-calendar3"></i>
                            {a.date?.seconds ? new Date(a.date.seconds * 1000).toLocaleDateString('id-ID') : '-'}
                            <span className="dot">•</span>
                            <span className="article-category">{displayCategory}</span>
                            <span className="dot">•</span>
                            <i className="bi bi-eye"></i>
                            {a.views || 0} views
                          </p>
                          <h5 className="fw-bold">{a.title}</h5>
                          <p className="text-muted small">{a.excerpt}</p>
                          <span className="article-cta">
                            Baca Selengkapnya →
                          </span>
                        </div>
                      </article>
                    </Link>
                  </div>
                )})
              ) : (
                <p className="text-center text-muted">Artikel tidak ditemukan.</p>
              )}
            </div>
          )}

          {!loading && !isAll && totalPages > 1 && (
            <div className="pagination-footer">
              <button
                className="btn btn-outline-success btn-sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              >
                ← Sebelumnya
              </button>
              <select
                className="form-select form-select-sm page-select"
                value={currentPage}
                onChange={e => setCurrentPage(Number(e.target.value))}
              >
                {Array.from({ length: totalPages }).map((_, idx) => (
                  <option key={idx + 1} value={idx + 1}>
                    Halaman {idx + 1} / {totalPages}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-outline-success btn-sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              >
                Berikutnya →
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
